/**
 * Wave 11.4 — Sobel 3×3 edge-detect fragment shader.
 *
 * Mirrors `src/pipeline/filters/spatial/edge-detect.js` exactly:
 *
 *   Gx = [ -1  0 +1 ;
 *          -2  0 +2 ;
 *          -1  0 +1 ]    (smooth Y, differentiate X)
 *   Gy = [ -1 -2 -1 ;
 *           0  0  0 ;
 *          +1 +2 +1 ]    (smooth X, differentiate Y)
 *
 *   magnitude = sqrt(Gx² + Gy²)   on Rec.709 luminance.
 *   if mag < threshold → mag = 0
 *   output (R,G,B) = (mag, mag, mag); alpha untouched.
 *
 * Boundary: mirror-extend via `mirrorIdx(i, n)`.
 *
 * Single-pass — 8 texture reads, two sums, one sqrt. Fits in every
 * mainstream WebGL2 driver. Performance is fragment-bound.
 *
 * References:
 *   • Sobel, I. (1968) "An Isotropic 3×3 Image Gradient Operator."
 */

export const FRAG_SOBEL = /* glsl */ `#version 300 es
precision highp float;
precision highp int;

uniform highp sampler2D uSrc;
uniform ivec2 uSrcSize;
uniform float uThreshold;

in vec2 vUv;
out vec4 outColor;

const float LUMA_R = 0.2126;
const float LUMA_G = 0.7152;
const float LUMA_B = 0.0722;

/* Mirror-extend index — single modulo into period 2(n-1) so the
 * result is correct for ARBITRARILY-far-out indices (matches CPU
 * _util.js::mirrorIndex exactly). Sobel only ever samples by ±1,
 * so the small-dim case is trivial here; we still use the canonical
 * form so all four shaders share identical boundary semantics. */
int mirrorIdx(int x, int n) {
    if (n <= 1) return 0;
    int period = 2 * (n - 1);
    int m = x % period;
    if (m < 0) m += period;
    return m < n ? m : period - m;
}

float lumaAt(int x, int y) {
    vec4 c = texelFetch(uSrc, ivec2(x, y), 0);
    return LUMA_R * c.r + LUMA_G * c.g + LUMA_B * c.b;
}

void main() {
    int x = int(floor(vUv.x * float(uSrcSize.x)));
    int y = int(floor(vUv.y * float(uSrcSize.y)));
    if (x >= uSrcSize.x) x = uSrcSize.x - 1;
    if (y >= uSrcSize.y) y = uSrcSize.y - 1;

    int xL = mirrorIdx(x - 1, uSrcSize.x);
    int xR = mirrorIdx(x + 1, uSrcSize.x);
    int yU = mirrorIdx(y - 1, uSrcSize.y);
    int yD = mirrorIdx(y + 1, uSrcSize.y);

    float tL = lumaAt(xL, yU);
    float tC = lumaAt(x,  yU);
    float tR = lumaAt(xR, yU);
    float mL = lumaAt(xL, y );
    float mR = lumaAt(xR, y );
    float bL = lumaAt(xL, yD);
    float bC = lumaAt(x,  yD);
    float bR = lumaAt(xR, yD);

    float gx = -tL + tR
               - 2.0 * mL + 2.0 * mR
               - bL + bR;
    float gy = -tL - 2.0 * tC - tR
               + bL + 2.0 * bC + bR;

    float mag = sqrt(gx * gx + gy * gy);
    if (mag < uThreshold) mag = 0.0;

    /* Preserve alpha from the centre sample so the editor's α-channel
     * invariant (always 1.0 on canonical srcFloat) is kept. */
    vec4 centre = texelFetch(uSrc, ivec2(x, y), 0);
    outColor = vec4(mag, mag, mag, centre.a);
}
`;
